import type { RefundInfo } from "@/api/order";

interface Props {
  refundInfo: RefundInfo;
}

/** 售后状态颜色映射 */
const STATUS_STYLE: Record<number, { color: string; bg: string }> = {
  0: { color: "#D97706", bg: "#FEF3C7" },    // 待处理 - 黄
  10: { color: "#DC2626", bg: "#FEE2E2" },   // 已驳回 - 红
  20: { color: "#059669", bg: "#D1FAE5" },   // 已退款 - 绿
  30: { color: "#6B7280", bg: "#F3F4F6" },   // 已取消 - 灰
};

export default function RefundInfoSection({ refundInfo }: Props) {
  const statusStyle =
    STATUS_STYLE[refundInfo.refund_status] ?? STATUS_STYLE[0];
  const isRejected = refundInfo.refund_status === 10;

  return (
    <div
      className="mx-4 mt-3 bg-white rounded-2xl p-4 mb-4"
      style={{ borderLeft: `3px solid ${statusStyle.color}` }}
    >
      {/* 标题行 */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-bold text-gray-900">售后信息</div>
        <span
          className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
          style={{ background: statusStyle.bg, color: statusStyle.color }}
        >
          {refundInfo.refund_status_text}
        </span>
      </div>

      {/* 信息列表 */}
      <div className="space-y-0">
        {/* 售后单号 */}
        <InfoRow label="售后单号" value={refundInfo.refund_no || "-"} mono />

        {/* 申请原因 */}
        <InfoRow label="申请原因" value={refundInfo.apply_reason || "-"} />

        {/* 申请说明 — 文字靠右 */}
        {refundInfo.apply_desc && (
          <div className="flex items-center justify-between py-2.5">
            <span className="text-xs text-gray-400 w-[72px] flex-shrink-0">
              申请说明
            </span>
            <span className="text-sm text-gray-700 leading-relaxed text-right max-w-[200px]">
              {refundInfo.apply_desc}
            </span>
          </div>
        )}

        {/* 上传图片 */}
        {refundInfo.apply_img && refundInfo.apply_img.length > 0 && (
          <div className="flex items-start py-2.5">
            <span className="text-xs text-gray-400 w-[72px] flex-shrink-0 pt-[18px]">
              凭证图片
            </span>
            <div className="flex gap-2 flex-wrap">
              {refundInfo.apply_img.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`凭证${i + 1}`}
                  className="w-16 h-16 rounded-lg object-cover cursor-pointer"
                  onClick={() => window.open(url, "_blank")}
                />
              ))}
            </div>
          </div>
        )}

        {/* 拒绝原因 — 仅已驳回时显示 */}
        {isRejected && refundInfo.refuse_desc && (
          <div className="flex items-center justify-between py-2.5">
            <span
              className="text-xs font-medium flex-shrink-0"
              style={{ width: 72, color: "#DC2626" }}
            >
              拒绝原因
            </span>
            <span
              className="text-sm leading-relaxed text-right max-w-[200px]"
              style={{ color: "#DC2626" }}
            >
              {refundInfo.refuse_desc}
            </span>
          </div>
        )}

        {/* 退款金额 */}
        <InfoRow
          label="退款金额"
          value={
            refundInfo.refund_money
              ? `¥${Number(refundInfo.refund_money).toLocaleString()}`
              : "-"
          }
          valueColor="#EF4444"
          valueBold
        />

        {/* 申请时间 */}
        <InfoRow label="申请时间" value={refundInfo.create_time || "-"} />
      </div>
    </div>
  );
}

/** 单行信息项 — label左 value右 */
function InfoRow({
  label,
  value,
  mono,
  valueColor = "#374151",
  valueBold = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  valueColor?: string;
  valueBold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-gray-400 w-[72px] flex-shrink-0">
        {label}
      </span>
      <span
        className={`text-sm ${mono ? "font-mono" : ""} ${
          valueBold ? "font-bold" : "font-medium"
        }`}
        style={{ color: valueColor }}
      >
        {value}
      </span>
    </div>
  );
}
